import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import PragmaSelect from "./PragmaSelect";

/**
 * CustomFieldsRenderer
 * Renders all admin-defined custom fields for a given entity ("company" | "contact")
 * inline below the standard fields panel. Saves on blur.
 *
 * Props:
 *  - entity: "company" | "contact"
 *  - recordId: id of the record
 *  - values: current custom_fields dict on the record
 *  - onSave: async (newValuesDict) => updatedRecord  (parent decides how to PATCH)
 */
export default function CustomFieldsRenderer({ entity, values = {}, onSave }) {
  const [fields, setFields] = useState([]);
  const [local, setLocal] = useState(values || {});

  useEffect(() => {
    setLocal(values || {});
  }, [values]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/custom-fields?entity=${entity}`);
        if (!cancelled) setFields(data.filter((f) => f.active));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [entity]);

  if (fields.length === 0) return null;

  const commit = async (key, newVal) => {
    const next = { ...local, [key]: newVal };
    setLocal(next);
    try {
      await onSave(next);
    } catch {
      toast.error("Failed to save");
    }
  };

  return (
    <div className="pg-card p-6" data-testid={`custom-fields-${entity}`}>
      <div className="mono text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--pg-text-mute)" }}>
        Custom Fields
      </div>
      <div className="grid grid-cols-2 gap-4">
        {fields.map((f) => {
          const v = local[f.key] ?? "";
          const testid = `custom-field-${f.key}`;
          return (
            <div key={f.id}>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">
                {f.label}{f.required && <span style={{ color: "var(--pg-red)" }}> *</span>}
              </label>

              {f.field_type === "text" && (
                <input className="pg-input" value={v} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                       onBlur={(e) => commit(f.key, e.target.value)} data-testid={testid} />
              )}
              {f.field_type === "number" && (
                <input className="pg-input" type="number" value={v} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                       onBlur={(e) => commit(f.key, e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />
              )}
              {f.field_type === "date" && (
                <input className="pg-input" type="date" value={v ? String(v).slice(0, 10) : ""}
                       onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                       onBlur={(e) => commit(f.key, e.target.value)} data-testid={testid} />
              )}
              {f.field_type === "url" && (
                <input className="pg-input" type="url" value={v} placeholder="https://…"
                       onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                       onBlur={(e) => commit(f.key, e.target.value)} data-testid={testid} />
              )}
              {f.field_type === "dropdown" && (
                <PragmaSelect
                  value={v}
                  onChange={(nv) => commit(f.key, nv)}
                  options={[{ value: "", label: "—" }, ...((f.options || []).map((o) => ({ value: o, label: o })))]}
                  placeholder="—"
                  testId={testid}
                  size="sm"
                />
              )}
              {f.field_type === "checkbox" && (
                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--pg-text)" }}>
                  <input type="checkbox" checked={!!v}
                         onChange={(e) => commit(f.key, e.target.checked)} data-testid={testid} />
                  <span className="text-mute">Enabled</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
